import React from 'react';
import { AtomVariant } from 'src/types/UserInterface';
import styles from './Button.module.scss';
import cn from 'classnames';
import { Icon, IconName } from 'src/components/common/Icon/Icon';

interface ButtonShared {
  children: React.ReactText;
  variant?: AtomVariant;
  prefix?: IconName;
  suffix?: IconName;
  ariaLabel?: string;
  disabled?: boolean;
}

interface ButtonNative extends ButtonShared {
  tag: 'button';
  type: 'button' | 'submit' | 'reset';
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

interface ButtonAnchor extends ButtonShared {
  tag: 'a';
  href?: string;
  target?: '_blank' | '_self' | '_parent' | '_top';
}

type ButtonProps = ButtonAnchor | ButtonNative;

const ButtonContent: React.FC<Pick<ButtonShared, 'prefix' | 'suffix'>> = ({ prefix, suffix, children }) => {
  const iconSize = 20;

  return (
    <React.Fragment>
      {prefix && <Icon name={prefix} size={iconSize} />}
      <span>{children}</span>
      {suffix && <Icon name={suffix} size={iconSize} />}
    </React.Fragment>
  );
};

export const Button: React.VFC<ButtonProps> = (props) => {
  const { children, variant = 'primary', prefix, suffix, ariaLabel, disabled } = props;
  const buttonContentProps = { prefix, suffix, children };
  const classname = cn(styles['o-button'], {
    [styles[`o-button--${variant}`]]: !disabled,
    [styles['is-disabled']]: disabled,
  });

  switch (props.tag) {
    case 'a':
      return (
        <a href={props.href} target={props.target} className={classname} aria-label={ariaLabel}>
          <ButtonContent {...buttonContentProps} />
        </a>
      );
    case 'button':
      return (
        <button
          onClick={props.onClick}
          type={props.type}
          className={classname}
          aria-label={ariaLabel}
          disabled={disabled}
        >
          <ButtonContent {...buttonContentProps} />
        </button>
      );
    default:
      throw new Error('Button tag must be either "a" or "button"');
  }
};
